/**
 * /challenge — Weekly Career Challenge (public, shareable LinkedIn link).
 * SPARK formula: Story → Insight → Challenge → RESMO Demo (free Opportunity
 * Decoder, no account) → CTA. Theme auto-rotates weekly; admin can override.
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "../lib/api";
import { Zap, Lightbulb, Target, Bot, Rocket, Loader, Flame, ArrowRight, Share2, Check, Lock } from "lucide-react";
import DecoderBrief from "../components/DecoderBrief";

const NAVY = "#0F1B2D";
const ORANGE = "#FF6A00";
const YELLOW = "#FFC145";
const CREAM = "#F6F1E7";

function StepCard({ icon: Icon, kicker, children, testid }) {
  return (
    <div className="bg-white border-[3px] p-5 sm:p-6" style={{ borderColor: NAVY, boxShadow: `5px 5px 0 0 ${NAVY}` }} data-testid={testid}>
      <div className="flex items-center gap-2 mb-2">
        <span className="inline-flex items-center justify-center w-7 h-7 rounded-full border-2"
              style={{ borderColor: ORANGE, color: ORANGE, background: "rgba(255,106,0,0.08)" }}>
          <Icon size={14} />
        </span>
        <span className="font-mono-rm text-[10px] uppercase tracking-[2px] font-bold" style={{ color: ORANGE }}>{kicker}</span>
      </div>
      {children}
    </div>
  );
}

function DecoderResult({ result }) {
  const career = result?.role_title ? `&career=${encodeURIComponent(result.role_title)}` : "";
  return <DecoderBrief result={result} ctaTo={`/early-access?src=decoder${career}`} />;
}

export default function Challenge() {
  const [c, setC] = useState(null);
  const [archive, setArchive] = useState([]);
  const [jd, setJd] = useState("");
  const [decoding, setDecoding] = useState(false);
  const [result, setResult] = useState(null);
  const [runId, setRunId] = useState(null);
  const [shareUrl, setShareUrl] = useState("");
  const [sharing, setSharing] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    axios.get(`${API}/challenge/current`).then((r) => setC(r.data)).catch(() => {});
    axios.get(`${API}/challenge/archive`).then((r) => setArchive(r.data?.weeks || [])).catch(() => {});
  }, []);

  const decode = async () => {
    setError("");
    if (jd.trim().length < 100) {
      setError("Paste the full job description — at least a few paragraphs so the Decoder has something to chew on.");
      return;
    }
    setDecoding(true);
    setResult(null);
    setRunId(null);
    setShareUrl("");
    setCopied(false);
    try {
      const res = await axios.post(`${API}/challenge/decoder`, { job_description: jd });
      setResult(res.data.result);
      setRunId(res.data.run_id || null);
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 429) {
        setError("You've used your 3 free decodes for today — come back tomorrow, or join early access for the full toolkit.");
      } else {
        setError(typeof detail === "string" ? detail : "The Decoder hiccuped — try again in a moment.");
      }
    } finally {
      setDecoding(false);
    }
  };

  const share = async () => {
    if (shareUrl) {
      try { await navigator.clipboard.writeText(shareUrl); setCopied(true); setTimeout(() => setCopied(false), 2500); } catch { /* shown below anyway */ }
      return;
    }
    if (!runId) return;
    setSharing(true);
    try {
      const res = await axios.post(`${API}/challenge/decoder/${runId}/share`);
      const url = `${window.location.origin}/api/d/${res.data.token}`;
      setShareUrl(url);
      try { await navigator.clipboard.writeText(url); setCopied(true); setTimeout(() => setCopied(false), 2500); } catch { /* shown below anyway */ }
    } catch {
      setError("Couldn't create the share link — try again in a moment.");
    } finally {
      setSharing(false);
    }
  };

  return (
    <div className="min-h-screen" style={{ background: CREAM }} data-testid="challenge-page">
      {/* Hero */}
      <div style={{ background: NAVY }} className="px-6 pt-12 pb-16">
        <div className="max-w-3xl mx-auto">
          <div className="font-mono-rm text-[10px] uppercase tracking-[3px] mb-6" style={{ color: YELLOW }}>
            · RESMO · Weekly Career Challenge {c?.visible ? `· Week ${c.week_number}` : ""}
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.05] tracking-tight text-white" data-testid="challenge-headline">
            This week's <span style={{ color: ORANGE }}>career challenge.</span>
          </h1>
          {c?.visible && (
            <div className="mt-5 inline-flex items-center gap-2 px-4 py-2 rounded-full border-2 font-black text-xs uppercase tracking-[2px]"
                 style={{ borderColor: ORANGE, color: YELLOW, background: "rgba(255,106,0,0.12)" }}
                 data-testid="challenge-theme-badge">
              <Flame size={13} style={{ color: ORANGE }} /> {c.label}
            </div>
          )}
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-6 -mt-8 pb-16 space-y-5">
        {!c ? (
          <div className="flex justify-center py-16 font-mono-rm text-xs uppercase tracking-widest" style={{ color: "#64748B" }}>
            <Loader size={14} className="animate-spin mr-2" /> Loading this week's challenge…
          </div>
        ) : c.visible === false ? (
          <div className="bg-white border-[3px] p-8 sm:p-10 text-center" style={{ borderColor: NAVY, boxShadow: `8px 8px 0 0 ${NAVY}` }} data-testid="challenge-coming-soon">
            <Flame size={40} className="mx-auto mb-4" style={{ color: ORANGE }} />
            <h2 className="text-lg md:text-lg font-black" style={{ color: NAVY }}>
              The first challenge drops soon.
            </h2>
            <p className="mt-3 text-sm max-w-md mx-auto" style={{ color: "#475569" }}>
              Every week, a new career challenge — plus a free taste of RESMO.
              Share your story now and you'll be first in line when it opens.
            </p>
            <Link to="/early-access?src=challenge-waitlist"
                  className="mt-6 inline-flex items-center gap-2 px-8 py-3.5 border-2 font-black text-xs uppercase tracking-[1.5px]"
                  style={{ background: ORANGE, borderColor: NAVY, color: "white", boxShadow: `4px 4px 0 0 ${NAVY}` }}
                  data-testid="challenge-coming-soon-cta">
              Share your story <ArrowRight size={13} />
            </Link>
            <div className="mt-6 font-mono-rm text-[10px] uppercase tracking-[2px]" style={{ color: "#94A3B8" }}>
              · Every career begins with a SPARK ·
            </div>
          </div>
        ) : (
          <>
            <StepCard icon={Zap} kicker="Story" testid="challenge-story">
              <p className="text-base md:text-lg font-bold leading-snug" style={{ color: NAVY }}>{c.story}</p>
            </StepCard>

            <StepCard icon={Lightbulb} kicker="Insight" testid="challenge-insight">
              <p className="text-sm leading-relaxed" style={{ color: "#334155" }}>{c.insight}</p>
            </StepCard>

            <StepCard icon={Target} kicker="Your challenge" testid="challenge-action">
              <p className="text-sm font-bold leading-relaxed" style={{ color: NAVY }}>{c.challenge}</p>
            </StepCard>

            {/* RESMO Demo — the free Opportunity Decoder */}
            <div className="bg-white border-[3px] p-5 sm:p-7" style={{ borderColor: ORANGE, boxShadow: `6px 6px 0 0 ${NAVY}` }} data-testid="challenge-demo">
              <div className="flex flex-wrap items-center gap-2 mb-1">
                <span className="inline-flex items-center justify-center w-7 h-7 rounded-full border-2"
                      style={{ borderColor: ORANGE, color: ORANGE, background: "rgba(255,106,0,0.08)" }}>
                  <Bot size={14} />
                </span>
                <span className="font-mono-rm text-[10px] uppercase tracking-[2px] font-bold" style={{ color: ORANGE }}>
                  Try this in RESMO
                </span>
                <span className="rm-tag text-[9px]" style={{ background: "#DCFCE7", borderColor: "#0E5A4A", color: "#0E5A4A" }}>
                  Free · no account needed
                </span>
              </div>
              <h2 className="text-lg md:text-lg font-black mt-2" style={{ color: NAVY }}>
                The Opportunity Decoder
              </h2>
              <p className="text-sm mt-1 leading-relaxed" style={{ color: "#475569" }} data-testid="challenge-demo-framing">
                {c.demo.framing}
              </p>

              {!result && (
                <>
                  <textarea
                    value={jd}
                    onChange={(e) => setJd(e.target.value)}
                    rows={7}
                    maxLength={15000}
                    placeholder="Paste the full job description here…"
                    className="mt-4 w-full border-2 p-3 text-sm outline-none"
                    style={{ borderColor: NAVY, background: "#FFFDF8", color: NAVY }}
                    data-testid="decoder-jd-input"
                  />
                  <div className="flex items-center justify-between mt-1">
                    <span className="font-mono-rm text-[10px]" style={{ color: "#94A3B8" }}>{jd.length.toLocaleString()} / 15,000</span>
                    <span className="font-mono-rm text-[10px]" style={{ color: "#94A3B8" }}>{c.decoder_daily_limit} free decodes per day</span>
                  </div>
                </>
              )}

              {error && (
                <div className="mt-3 border-2 p-3 text-sm font-bold"
                     style={{ borderColor: "#B91C1C", background: "#FEE2E2", color: "#B91C1C" }}
                     data-testid="decoder-error">
                  {error}
                </div>
              )}

              {!result ? (
                <button onClick={decode} disabled={decoding}
                        className="mt-4 w-full py-3.5 border-2 font-black text-sm uppercase tracking-[1.5px] transition-transform active:translate-y-0.5 disabled:opacity-60"
                        style={{ background: ORANGE, borderColor: NAVY, color: "white", boxShadow: `4px 4px 0 0 ${NAVY}` }}
                        data-testid="decoder-run-btn">
                  {decoding ? (
                    <span className="inline-flex items-center gap-2"><Loader size={14} className="animate-spin" /> Decoding — under a minute…</span>
                  ) : (
                    "Decode this opportunity"
                  )}
                </button>
              ) : (
                <>
                  <DecoderResult result={result} />
                  {runId && (
                    <div className="mt-4 border-2 border-dashed p-4" style={{ borderColor: NAVY, background: "#FFFDF8" }}
                         data-testid="decoder-share-block">
                      <div className="flex flex-wrap items-center gap-3">
                        <div className="min-w-0 flex-1">
                          <div className="font-mono-rm text-[10px] uppercase tracking-[2px] font-bold" style={{ color: ORANGE }}>
                            · Proud of this decode?
                          </div>
                          <div className="text-sm font-bold mt-0.5" style={{ color: NAVY }}>
                            Share this brief — friends get a read-only link, no account needed.
                          </div>
                        </div>
                        <button onClick={share} disabled={sharing}
                                className="inline-flex items-center gap-2 px-5 py-2.5 border-2 font-black text-xs uppercase tracking-[1.5px] flex-shrink-0 disabled:opacity-60"
                                style={{ background: copied ? "#0E5A4A" : NAVY, borderColor: NAVY, color: copied ? "white" : YELLOW }}
                                data-testid="decoder-share-btn">
                          {sharing ? <Loader size={13} className="animate-spin" /> : copied ? <Check size={13} /> : <Share2 size={13} />}
                          {copied ? "Link copied!" : shareUrl ? "Copy link again" : "Share this brief"}
                        </button>
                      </div>
                      {shareUrl && (
                        <div className="mt-3 font-mono-rm text-[11px] break-all px-3 py-2 border"
                             style={{ borderColor: "#CBD5E1", background: "white", color: "#475569" }}
                             data-testid="decoder-share-url">
                          {shareUrl}
                        </div>
                      )}
                    </div>
                  )}
                  <button onClick={() => { setResult(null); setJd(""); setRunId(null); setShareUrl(""); }}
                          className="mt-4 w-full py-2.5 border-2 font-bold text-xs uppercase tracking-[1.5px]"
                          style={{ background: "white", borderColor: NAVY, color: NAVY }}
                          data-testid="decoder-again-btn">
                    Decode another job description
                  </button>
                </>
              )}
            </div>

            {/* Locked tool teaser */}
            {c.locked_tool && (
              <div className="border-[3px] p-5 sm:p-6" style={{ background: NAVY, borderColor: NAVY, boxShadow: `5px 5px 0 0 ${ORANGE}` }} data-testid="challenge-locked-tool">
                <div className="flex items-center gap-2 mb-2">
                  <span className="inline-flex items-center justify-center w-7 h-7 rounded-full border-2"
                        style={{ borderColor: YELLOW, color: YELLOW, background: "rgba(255,193,69,0.1)" }}>
                    <Lock size={13} />
                  </span>
                  <span className="font-mono-rm text-[10px] uppercase tracking-[2px] font-bold" style={{ color: YELLOW }}>
                    This week's locked tool · Coming with early access
                  </span>
                </div>
                <h3 className="text-lg md:text-lg font-black text-white" data-testid="challenge-locked-tool-name">
                  {c.locked_tool.name}
                </h3>
                <p className="text-sm mt-1 leading-relaxed" style={{ color: "#94A3B8" }}>
                  {c.locked_tool.description}
                </p>
                <Link to="/early-access?src=challenge-tool"
                      className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 border-2 font-black text-xs uppercase tracking-[1.5px]"
                      style={{ background: "transparent", borderColor: ORANGE, color: ORANGE }}
                      data-testid="challenge-locked-tool-cta">
                  Save my seat <ArrowRight size={13} />
                </Link>
              </div>
            )}

            <StepCard icon={Rocket} kicker="Go create a SPARK" testid="challenge-cta">
              <p className="text-sm leading-relaxed" style={{ color: "#334155" }}>
                Done with the challenge? Tell us the story behind it — and grab your early-access seat.
              </p>
              <Link to="/early-access"
                    className="mt-3 inline-flex items-center gap-2 px-6 py-3 border-2 font-black text-xs uppercase tracking-[1.5px]"
                    style={{ background: NAVY, borderColor: NAVY, color: YELLOW }}
                    data-testid="challenge-cta-btn">
                Share your story <ArrowRight size={13} />
              </Link>
            </StepCard>

            {/* Series archive */}
            {archive.length > 0 && (
              <div className="pt-8" data-testid="challenge-archive">
                <div className="text-center mb-5">
                  <h2 className="text-lg md:text-lg font-black" style={{ color: NAVY }}>The SPARK series so far</h2>
                  <p className="text-sm mt-1" style={{ color: "#64748B" }}>Catch up on every week's challenge.</p>
                </div>
                <div className="space-y-3">
                  {archive.map((w) => (
                    <details key={w.week_number} className="bg-white border-[3px] group"
                             style={{ borderColor: NAVY, boxShadow: `4px 4px 0 0 ${NAVY}` }}
                             open={w.is_current}
                             data-testid={`challenge-archive-week-${w.week_number}`}>
                      <summary className="flex flex-wrap items-center gap-2 px-5 py-3.5 cursor-pointer select-none list-none">
                        <span className="font-mono-rm text-[10px] uppercase tracking-[2px] font-bold" style={{ color: ORANGE }}>
                          Week {w.week_number}
                        </span>
                        <span className="font-black text-sm" style={{ color: NAVY }}>{w.label}</span>
                        {w.is_current && (
                          <span className="rm-tag text-[9px]" style={{ background: YELLOW, borderColor: NAVY, color: NAVY }}>
                            This week
                          </span>
                        )}
                        <span className="ml-auto text-xs font-bold group-open:hidden" style={{ color: "#94A3B8" }}>Open</span>
                      </summary>
                      <div className="px-5 pb-5 space-y-3 border-t-2 border-dashed pt-4" style={{ borderColor: "#E2E8F0" }}>
                        <p className="text-sm font-bold" style={{ color: NAVY }}>{w.story}</p>
                        <p className="text-sm" style={{ color: "#334155" }}>{w.insight}</p>
                        <div className="border-2 p-3" style={{ borderColor: NAVY, background: "#FFF7ED" }}>
                          <span className="font-mono-rm text-[10px] uppercase tracking-[2px] font-bold" style={{ color: ORANGE }}>Challenge · </span>
                          <span className="text-sm font-bold" style={{ color: NAVY }}>{w.challenge}</span>
                        </div>
                        <div className="flex items-center gap-2 text-xs" style={{ color: "#64748B" }}>
                          <Lock size={12} /> Unlocks with early access: <strong style={{ color: NAVY }}>{w.locked_tool?.name}</strong>
                        </div>
                      </div>
                    </details>
                  ))}
                </div>
              </div>
            )}

            <div className="text-center pt-6">
              <div className="inline-flex items-center gap-2 font-bold text-sm italic" style={{ color: ORANGE }}>
                <Flame size={16} /> Every career begins with a SPARK.
              </div>
              <div className="mt-3 font-mono-rm text-[10px] uppercase tracking-[2px]" style={{ color: "#94A3B8" }}>
                RESMO · A new challenge every week · Beyond the resume
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
